import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SearchResults, PropertyResult } from './SearchResults';
import { MapView } from './MapView';
import { ReactComponent as ListFillIcon } from '../icons/list-fill.svg';
import { ReactComponent as MapPinLineFill } from '../../../booking-bar/src/icons/map-pin-line-fill.svg';
import { ReactComponent as ArrowDownIcon } from '../../../booking-filters/src/icons/arrow-down.svg';
import { ReactComponent as PlusIcon } from '../../../booking-bar/src/icons/plus-bold.svg';
import {
	applyLockedScope,
	dispatchOceanSearch,
	dispatchOpenFilters,
	getSearchParamsFromUrl,
	OceanLockedScope,
	OceanSearchParams,
	OCEAN_SEARCH_EVENT,
	} from '../../../_shared/oceanSearchContext';

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: '', label: 'Default' },
	{ value: 'price', label: 'Price' },
	{ value: 'title', label: 'Title' },
	];

interface PropertyResultsProps
	{
	ajaxUrl: string;
	lockedScope?: OceanLockedScope | null;
	googleMapsApiKey?: string;
	}

export const PropertyResults = ({ ajaxUrl, lockedScope = null, googleMapsApiKey = '' }: PropertyResultsProps) =>
	{
	const [results, setResults] = useState<PropertyResult[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
	const [sort, setSort] = useState('');
	const [isSortOpen, setIsSortOpen] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const sortFieldRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (sortFieldRef.current && !sortFieldRef.current.contains(event.target as Node))
				{
				setIsSortOpen(false);
				}
			};
		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
		}, []);

	const runSearch = useCallback(async (params: Partial<OceanSearchParams>) => {
		setIsLoading(true);
		setHasSearched(true);
		setSort(params.sort || '');
		// Any new search or re-sort reorders the whole set, so the old page
		// number would point at an unrelated slice -- back to the first page.
		setCurrentPage(1);

		const query = new URLSearchParams({ action: 'search_properties' });
		Object.entries(params).forEach(([key, value]) => {
			if (value)
				{
				query.set(key, value);
				}
			});

		try
			{
			const response = await fetch(`${ajaxUrl}?${query}`);
			const data = await response.json();
			if (data.success)
				{
				setResults(data.data.results);
				}
			}
		catch (error)
			{
			console.error('Search error:', error);
			}
		finally
			{
			setIsLoading(false);
			}
		}, [ajaxUrl]);

	// On mount: reconstruct whatever search produced the current URL (direct
	// link, reload, or a page whose own lockedScope pins it to one term).
	// With nothing to reconstruct -- landing on the search page cold -- that
	// resolves to an empty param set, which the endpoint reads as "no
	// filters" and answers with every published property. That's the point:
	// the page shows the full list up front and the booking bar narrows it,
	// rather than opening on a results area that isn't there yet.
	useEffect(() => {
		runSearch(applyLockedScope(getSearchParamsFromUrl(), lockedScope));
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

	const handleSortChange = useCallback((value: string) => {
		setIsSortOpen(false);
		dispatchOceanSearch({ ...getSearchParamsFromUrl(), sort: value });
		}, []);

	useEffect(() => {
		const handleSearchEvent = (event: Event) => {
			const detail = (event as CustomEvent<Partial<OceanSearchParams>>).detail || {};
			runSearch(applyLockedScope(detail, lockedScope));
			};
		document.addEventListener(OCEAN_SEARCH_EVENT, handleSearchEvent);
		return () => document.removeEventListener(OCEAN_SEARCH_EVENT, handleSearchEvent);
		}, [runSearch, lockedScope]);

	if (!hasSearched)
		{
		return null;
		}

	return (
		<div className="property-results">
			<div className="property-results__header">
				<p className="property-results__count">
					{isLoading ? 'Searching…' : `${results.length} ${results.length === 1 ? 'property' : 'properties'} found`}
				</p>

				{viewMode === 'list' && (
					<div className="property-results__sort" ref={sortFieldRef}>
						<span className="property-results__sort-label">Sort by</span>
						<button
							type="button"
							className="property-results__sort-trigger"
							onClick={() => setIsSortOpen(!isSortOpen)}
							aria-expanded={isSortOpen}
						>
							<span>{SORT_OPTIONS.find((option) => option.value === sort)?.label || 'Default'}</span>
							<ArrowDownIcon className={`property-results__sort-arrow ${isSortOpen ? 'is-open' : ''}`} />
						</button>
						{isSortOpen && (
							<ul className="property-results__sort-list">
								{SORT_OPTIONS.map((option) => (
									<li key={option.value}>
										<button
											type="button"
											className={`property-results__sort-option ${sort === option.value ? 'is-selected' : ''}`}
											onClick={() => handleSortChange(option.value)}
										>
											{option.label}
										</button>
									</li>
								))}
							</ul>
						)}
					</div>
				)}

				<div className="property-results__view-toggle" role="group" aria-label="Results view">
					<button
						type="button"
						className={`property-results__view-toggle-btn ${viewMode === 'list' ? 'is-active' : ''}`}
						onClick={() => setViewMode('list')}
					>
						<span>List</span>
						<ListFillIcon />
					</button>
					<button
						type="button"
						className={`property-results__view-toggle-btn ${viewMode === 'map' ? 'is-active' : ''}`}
						onClick={() => {
							setViewMode('map');
							setIsSortOpen(false);
							}}
					>
						<span>Map</span>
						<MapPinLineFill />
					</button>
				</div>

				<button type="button" className="property-results__filter-trigger" onClick={dispatchOpenFilters}>
					<span>Filter</span>
					<PlusIcon />
				</button>
			</div>

			{viewMode === 'list' ? (
				<SearchResults
					results={results}
					isLoading={isLoading}
					currentPage={currentPage}
					onPageChange={setCurrentPage}
				/>
			) : (
				// Mounted only while active -- Google Maps sizes itself off
				// the container's rendered dimensions, which are wrong (0) if
				// initialized while display:none.
				results.length > 0 && (
					<MapView
						properties={results}
						apiKey={googleMapsApiKey}
					/>
				)
			)}
		</div>
		);
	};
